import React, { useState } from 'react';

const formatCurrency = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (date = new Date()) => {
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const generateInvoiceNumber = () => {
  const now = new Date();
  const timestamp = now.getTime().toString().slice(-6);
  return `INV-${timestamp}`;
};

export default function PosPrinter({ 
  isOpen, 
  onClose, 
  cartItems, 
  userData, 
  total, 
  subtotal, 
  tax,
  onPrintComplete,
  autoPrintNow = false,
  backdropZIndex = 10001
}) {
  const [printOption, setPrintOption] = useState('later'); // 'later' or 'now'
  const [printing, setPrinting] = useState(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoUrl = `${origin}/Gaming Science logo.webp`;
  const scannerUrl = `${origin}/frame.png`;

  const handlePrint = async () => {
    if (printOption === 'later') {
      // Just close the modal, transaction will be marked as pending
      onPrintComplete('later');
      onClose();
      return;
    }

    setPrinting(true);
    
    try {
      // Create print content
      const printContent = generatePrintContent();
      
      // Open print dialog
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait a bit for print dialog to open. Use onafterprint to close window when done to avoid extra blank page issues.
      const onAfter = () => {
        try { printWindow.close(); } catch (e) {}
        setPrinting(false);
        onPrintComplete('now');
        onClose();
      };

      setTimeout(() => {
        printWindow.print();
        // attach afterprint if available
        try {
          printWindow.onafterprint = onAfter;
        } catch (e) {
          // fallback: close after short delay
          setTimeout(onAfter, 800);
        }
      }, 300);
      
    } catch (error) {
      console.error('Print error:', error);
      setPrinting(false);
      alert('Print failed. Please try again.');
    }
  };

  // Auto-trigger print when modal opens and autoPrintNow is requested
  React.useEffect(() => {
    // Hooks must run regardless of isOpen state to satisfy rules-of-hooks
    if (!isOpen) return undefined;
    if (!autoPrintNow) return undefined;
    // set option to now and trigger print shortly after modal open
    setPrintOption('now');
    const t = setTimeout(() => {
      handlePrint();
    }, 300);
    return () => clearTimeout(t);
  }, [isOpen, autoPrintNow]);

  // After hooks are declared, we can conditionally render
  if (!isOpen) return null;

  const invoiceNumber = generateInvoiceNumber();
  const currentDate = formatDate();

  const generatePrintContent = () => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Gaming Science - Receipt</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 0;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          html, body {
            height: auto;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
          }

          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.2;
            color: #000;
            background: #fff;
            width: 58mm;
            padding: 4px; /* reduce padding to avoid extra whitespace */
            overflow: visible;
          }
          
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }
          
          .header img {
            width: 120px;
            max-width: 100%;
            margin: 0 auto 6px;
          }
          
          .header p {
            font-size: 10px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.6px;
          }
          
          .receipt-info {
            margin-bottom: 4px;
            font-size: 10px;
          }
          
          .receipt-info div {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
          }
          
          .items {
            border-bottom: 1px dashed #000;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }
          
          .item {
            margin-bottom: 4px;
            font-size: 10px;
          }
          
          .item-name {
            font-weight: bold;
            margin-bottom: 1px;
          }
          
          .item-details {
            color: #666;
            margin-bottom: 1px;
          }
          
          .item-price {
            text-align: right;
            font-weight: bold;
          }
          
          .summary {
            margin-bottom: 4px;
          }
          
          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
            font-size: 10px;
          }
          
          .total-row {
            font-weight: bold;
            font-size: 12px;
            border-top: 1px solid #000;
            padding-top: 4px;
            margin-top: 4px;
          }
          
          .thank-you {
            text-align: center;
            margin: 4px 0;
            font-size: 11px;
            font-weight: bold;
          }
          
          .divider {
            text-align: center;
            margin: 2px 0;
            color: #666;
          }
          
          .qr-message {
            text-align: center;
            margin-top: 8px;
            font-size: 9px;
            color: #333;
          }
          
          .qr-message img {
            width: 75px;
            max-width: 100%;
            margin-bottom: 6px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${logoUrl}" alt="Gaming Science" />
          <p>Gaming Zone</p>
          <p style="font-size:9px;color:#444;margin-top:2px;">Main Road, Golamber, Buxar</p>
        </div>
        
        <div class="receipt-info">
          <div>
            <span>Invoice:</span>
            <span>${invoiceNumber}</span>
          </div>
          <div>
            <span>Date:</span>
            <span>${currentDate}</span>
          </div>
          <div>
            <span>Customer:</span>
            <span>${userData.name}</span>
          </div>
          <div>
            <span>Phone:</span>
            <span>${userData.phone}</span>
          </div>
        </div>
        
        <div class="items">
          ${cartItems.map(item => `
            <div class="item">
              <div class="item-name">${item.name}</div>
              <div class="item-details">${item.duration}</div>
              <div class="item-price">${formatCurrency(item.price)}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="summary">
          <div class="summary-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(subtotal)}</span>
          </div>
          <!-- Tax removed -->
          <div class="summary-row total-row">
            <span>TOTAL:</span>
            <span>${formatCurrency(total)}</span>
          </div>
        </div>
        
        <div class="thank-you">
          Thank you for playing!
        </div>
        
        <div class="divider">
          • • • • • • • • • • • • • • • • • • • •
        </div>
        
        <div class="qr-message">
          <img src="${scannerUrl}" alt="Scan code" />
          <div>Scan this to book or view game menu</div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div
      className="pos-printer-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: backdropZIndex
      }}
    >
      <div
        className="pos-printer-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 'var(--border-radius-lg)',
          padding: '32px',
          maxWidth: '500px',
          width: '90vw',
          boxShadow: 'var(--neon-glow)'
        }}
      >
        <div className="pos-printer-header" style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            margin: 0, 
            color: 'var(--text-primary)', 
            fontSize: '1.5rem',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            🖨️ Print Receipt
          </h2>
          <p style={{ 
            margin: '8px 0 0 0', 
            color: 'var(--text-secondary)',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            Choose when to print your receipt
          </p>
        </div>

        <div className="pos-printer-body">
          {/* Receipt Preview */}
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            fontFamily: 'Courier New, monospace',
            fontSize: '10px',
            lineHeight: '1.2',
            color: '#000',
            maxHeight: '300px',
            overflow: 'auto'
          }}>
            <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '8px', marginBottom: '8px' }}>
              <img
                src={logoUrl}
                alt="Gaming Science"
                style={{ width: '110px', maxWidth: '100%', margin: '0 auto 6px' }}
              />
              <div style={{ fontSize: '9px', color: '#666', letterSpacing: '0.6px', textTransform: 'uppercase' }}>Gaming Zone</div>
              <div style={{ fontSize: '8px', color: '#444', marginTop: '2px' }}>Main Road, Golamber, Buxar</div>
            </div>
            
            <div style={{ marginBottom: '8px', fontSize: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Invoice:</span>
                <span>{invoiceNumber}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Date and Time:</span>
                <span>{currentDate}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Customer:</span>
                <span>{userData.name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span>Phone:</span>
                <span>{userData.phone}</span>
              </div>
            </div>
            
            <div style={{ borderBottom: '1px dashed #000', paddingBottom: '8px', marginBottom: '8px' }}>
              {cartItems.map((item, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '1px' }}>{item.name}</div>
                  <div style={{ color: '#666', marginBottom: '1px' }}>{item.duration}</div>
                  <div style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(item.price)}</div>
                </div>
              ))}
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px', fontSize: '8px' }}>
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #000', paddingTop: '4px', marginTop: '4px', fontWeight: 'bold', fontSize: '10px' }}>
                <span>TOTAL:</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
            
            <div style={{ textAlign: 'center', margin: '8px 0', fontSize: '9px', fontWeight: 'bold' }}>
              Thank you for playing!
            </div>
            
            <div style={{ textAlign: 'center', margin: '4px 0', color: '#666', fontSize: '8px' }}>
              • • • • • • • • • • • • • • • • • • • •
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <img
                src={scannerUrl}
                alt="Scan code"
                style={{ width: '70px', maxWidth: '100%', marginBottom: '6px' }}
              />
              <div style={{ fontSize: '8px', color: '#333' }}>Scan this to book or view game menu</div>
            </div>
          </div>

          {/* Print Options */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: 'var(--text-primary)', 
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              Print Options
            </h3>
            
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: printOption === 'now' ? 'rgba(0, 229, 255, 0.1)' : 'var(--input-bg)',
                border: `1px solid ${printOption === 'now' ? 'var(--accent)' : 'var(--card-border)'}`,
                borderRadius: 'var(--border-radius-sm)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="printOption"
                  value="now"
                  checked={printOption === 'now'}
                  onChange={(e) => setPrintOption(e.target.value)}
                  style={{ margin: 0 }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                    🖨️ Print Now
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Print receipt immediately on connected POS printer
                  </div>
                </div>
              </label>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: printOption === 'later' ? 'rgba(0, 229, 255, 0.1)' : 'var(--input-bg)',
                border: `1px solid ${printOption === 'later' ? 'var(--accent)' : 'var(--card-border)'}`,
                borderRadius: 'var(--border-radius-sm)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="printOption"
                  value="later"
                  checked={printOption === 'later'}
                  onChange={(e) => setPrintOption(e.target.value)}
                  style={{ margin: 0 }}
                />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                    ⏰ Print Later
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Complete payment, print receipt later at counter
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="pos-printer-footer" style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              color: 'var(--text-secondary)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-sm)',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing}
            style={{
              padding: '12px 24px',
              background: 'var(--accent)',
              color: '#0b1735',
              border: 'none',
              borderRadius: 'var(--border-radius-sm)',
              cursor: printing ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              opacity: printing ? 0.7 : 1
            }}
          >
            {printing ? 'Printing...' : printOption === 'now' ? 'Print Now' : 'Complete Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
